// TB-109 (A): pure helpers for the one-screen doctor schedule.
// Dates are local "YYYY-MM-DD" strings and times "HH:mm" wall-clock strings,
// the same shapes providers-api returns (schedule.consultationDate, slot.startTime).
import moment from 'moment'
import { visitKindOf, visitTypeLabel, kindLabel, activeVisitTypes } from '@/utils/visitTypes'
import { ISO, WEEK_START, WEEKEND_DAYS, clinicNow, formatClock, toWallPayload } from '@/utils/locale'

export { ISO }
export const SLOT_LENGTHS = [15, 20, 30, 45, 60]
// TB-178: 23:59 is end of day. A range ending there keeps a last slot clamped to 23:59
// (23:30 to 23:59) when the remainder is at least MIN_END_SLOT minutes; same rule as providers-api buildSlots.
export const END_OF_DAY = 23 * 60 + 59
export const MIN_END_SLOT = 15
export function slotCount (startMin, endMin, len) {
  const n = Math.floor((endMin - startMin) / len)
  return n + (endMin === END_OF_DAY && endMin - (startMin + n * len) >= MIN_END_SLOT ? 1 : 0)
}
const ACTIVE = ['booked', 'rescheduled']
const ENDED = ['completed', 'awaiting-invoice', 'canceled', 'cancelled', 'reject', 'over-due']


export function toMin (hhmm) {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(hhmm || ''))
  return m ? Number(m[1]) * 60 + Number(m[2]) : null
}

export function fromMin (min) {
  const h = Math.floor(min / 60)
  const m = min % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

// "09:30" -> "9:30 AM"
export const fmtTime = formatClock

// Appointment ISO ("2026-10-22T09:30:00.000Z") carries the wall-clock time in UTC fields.
export function apptDate (iso) {
  return String(iso || '').slice(0, 10)
}
export function apptTime (iso) {
  const m = /T(\d{2}):(\d{2})/.exec(String(iso || ''))
  return m ? `${m[1]}:${m[2]}` : ''
}

// TB-121: weeks start on Sunday; Friday and Saturday are the weekend (TB-123: constants in utils/locale).
function sundayOf (m) {
  return m.clone().subtract((m.day() - WEEK_START + 7) % 7, 'day').startOf('day')
}

// Sunday-first week containing `day`
export function weekDays (day) {
  const start = sundayOf(moment(day, ISO))
  return Array.from({ length: 7 }, (_, i) => start.clone().add(i, 'day').format(ISO))
}

// 6 x 7 grid of dates for the month of `day`, Sunday first
export function monthGrid (day) {
  const start = sundayOf(moment(day, ISO).startOf('month'))
  return Array.from({ length: 42 }, (_, i) => start.clone().add(i, 'day').format(ISO))
}

export function isWeekend (date) {
  return WEEKEND_DAYS.includes(moment(date, ISO).day())
}

// Every "YYYY-MM" a list of dates touches (schedule?period= is per month)
export function periodsFor (dates) {
  return [...new Set((dates || []).map((d) => String(d).slice(0, 7)))]
}

// TB-184: visual kind and display name of a consultation type come from the
// admin-managed type (utils/visitTypes.js); the title mapping is only its fallback.
// typeKey takes a type record, id, title, display name or kind key.
export function typeKey (ref) {
  return visitKindOf(ref)
}
export const TYPE_LABEL = {
  get clinic () { return kindLabel('clinic') },
  get video () { return kindLabel('video') },
  get home () { return kindLabel('home') }
}

// UI label for a consultation type (record, id, title or name); the API value is kept.
export function typeName (ref) {
  return visitTypeLabel(ref)
}

// Types offered when adding hours: the active ones, in the admin's sort order.
export function scheduleTypes (list) {
  return activeVisitTypes(list)
}

export function isActive (status) {
  return ACTIVE.includes(status)
}
export function isCancelled (status) {
  return ['canceled', 'cancelled', 'reject'].includes(status)
}

export const STATUS_META = {
  booked: { label: 'Booked', tone: 'info' },
  rescheduled: { label: 'Rescheduled', tone: 'primary' },
  'awaiting-invoice': { label: 'Awaiting invoice', tone: 'warning' },
  completed: { label: 'Completed', tone: 'success' },
  canceled: { label: 'Cancelled', tone: 'danger' },
  cancelled: { label: 'Cancelled', tone: 'danger' },
  reject: { label: 'Rejected', tone: 'danger' },
  'over-due': { label: 'Overdue', tone: 'warning' },
  pending: { label: 'Pending', tone: 'neutral' },
  'pending-payment': { label: 'Awaiting payment', tone: 'neutral' }
}
export function statusMeta (status) {
  return STATUS_META[status] || { label: String(status || 'Unknown'), tone: 'neutral' }
}

// A video visit the doctor or clinic books stays "pending" until the patient pays
// (the patient gets a pay-now notification; payment turns it booked).
export const AWAITING_PAYMENT = {
  label: 'Waiting for the patient to pay',
  short: 'Awaiting payment',
  hint: 'The patient got a pay-now notification. It turns Booked once paid.',
  tone: 'warning'
}
export function awaitingPayment (appt) {
  return !!appt && appt.status === 'pending' && appt.type === 'video'
}

export function patientName (appt) {
  const p = (appt && appt.patient) || {}
  const name = [p.firstName, p.lastName].filter(Boolean).join(' ').trim()
  return name || 'Patient'
}

/**
 * Builds the per-day model the views draw from.
 * schedules: GET schedule?period= rows; appointments: GET appointments/by-period rows.
 * Returns { [date]: { blocks: [...], appts: [...], booked, free } }.
 * Cancelled appointments are left out of the chips (their slot is free again).
 */
export function buildDays (schedules, appointments, typeTitles = {}, now = clinicNow()) {
  const days = {}
  const day = (d) => (days[d] = days[d] || { blocks: [], appts: [], booked: 0, free: 0 })
  const nowDate = now.format(ISO)
  const nowMin = now.hours() * 60 + now.minutes()
  ;(schedules || []).forEach((s) => {
    if (!s || !s.consultationDate) return
    const d = day(s.consultationDate)
    const slots = (s.slots || []).filter((x) => !x.deletedAt)
    const bookedSlots = slots.filter((x) => x.status === false && x.appointment && !isCancelled(x.appointment.status))
    const past = (x) => s.consultationDate < nowDate || (s.consultationDate === nowDate && toMin(x.startTime) <= nowMin)
    const free = slots.filter((x) => x.status !== false && !past(x)).length
    const title = (s.consultationType && s.consultationType.title) || ''
    d.blocks.push({
      id: s.id,
      date: s.consultationDate,
      start: String(s.startTime || '').slice(0, 5),
      end: String(s.endTime || '').slice(0, 5),
      perSlotTime: Number(s.perSlotTime) || 0,
      perSlotAmount: Number(s.perSlotAmount) || 0,
      typeId: s.consultationType && s.consultationType.id,
      typeTitle: title,
      type: typeKey(s.consultationType || title),
      posId: s.purposes && s.purposes.id,
      purpose: (s.purposes && s.purposes.poc && s.purposes.poc.title) || '',
      slots,
      bookedSlotIds: bookedSlots.map((x) => x.id),
      booked: bookedSlots.length,
      free,
      // TB-131: the practice of these hours (null establishment = own clinic)
      practiceId: s.establishmentId || (s.establishment && s.establishment.id) || 'own'
    })
    d.booked += bookedSlots.length
    d.free += free
  })
  ;(appointments || []).forEach((a) => {
    if (!a || !a.startTime || isCancelled(a.status)) return
    const date = apptDate(a.startTime)
    const title = typeTitles[a.consultationTypeId] || (a.consultationType && a.consultationType.title) || ''
    day(date).appts.push({
      id: a.id,
      date,
      start: apptTime(a.startTime),
      end: apptTime(a.endTime),
      status: a.status,
      confirmed: !!a.confirmedAt,
      type: typeKey(a.consultationType || a.consultationTypeId || title),
      typeTitle: title,
      name: patientName(a),
      practiceId: a.establishmentId || 'own', // TB-131
      raw: a
    })
  })
  Object.values(days).forEach((d) => {
    d.blocks.sort((a, b) => toMin(a.start) - toMin(b.start))
    d.appts.sort((a, b) => toMin(a.start) - toMin(b.start))
  })
  return days
}

// Month cell: at most `max` chips, the rest as one "+N more" summary
export function chipSummary (appts, max = 2) {
  const list = appts || []
  if (list.length <= max) return { shown: list, more: 0 }
  return { shown: list.slice(0, max - 1), more: list.length - (max - 1) }
}

// "Layla Hassan" -> "Layla H." so week chips fit a narrow column (full name in the title and panel)
export function shortName (name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean)
  if (parts.length < 2) return parts[0] || ''
  return `${parts[0]} ${parts[parts.length - 1][0].toUpperCase()}.`
}

// Month cell chips: one per type for open bookings, one for finished visits.
// [{ key: 'clinic' | 'video' | 'home' | 'done', count, label }]
export function groupAppts (appts) {
  const order = ['clinic', 'video', 'home', 'done']
  const counts = {}
  ;(appts || []).forEach((a) => {
    const key = ['completed', 'awaiting-invoice'].includes(a.status) ? 'done' : a.type
    counts[key] = (counts[key] || 0) + 1
  })
  const word = { clinic: kindLabel('clinic').toLowerCase(), video: kindLabel('video').toLowerCase(), home: kindLabel('home').toLowerCase(), done: 'done' }
  return order.filter((k) => counts[k]).map((k) => ({
    key: k,
    count: counts[k],
    label: k === 'home' && counts[k] > 1 ? `${counts[k]} ${word[k]}s` : `${counts[k]} ${word[k]}`
  }))
}

// Grid hours for the week view: 08:00..17:00 widened to cover data
export function gridHours (days, dates) {
  let min = 8 * 60
  let max = 17 * 60
  ;(dates || []).forEach((date) => {
    const d = days[date]
    if (!d) return
    d.blocks.forEach((b) => { min = Math.min(min, toMin(b.start)); max = Math.max(max, toMin(b.end)) })
    d.appts.forEach((a) => { min = Math.min(min, toMin(a.start)); max = Math.max(max, toMin(a.end) || toMin(a.start) + 30) })
  })
  return { from: Math.floor(min / 60), to: Math.min(24, Math.ceil(max / 60)) }
}

/**
 * Dates to create a block on. The API's repeat=7 stops at the end of the
 * current month, so the screen expands the range itself and sends one
 * repeat=0 create per date.
 * mode: 'once' | 'weekly' | 'daily'; until: last date (inclusive).
 */
export function repeatDates (start, mode, until, { maxDays = 366 } = {}) {
  const first = moment(start, ISO, true)
  if (!first.isValid()) return []
  if (mode !== 'weekly' && mode !== 'daily') return [first.format(ISO)]
  const last = moment(until, ISO, true)
  if (!last.isValid() || last.isBefore(first)) return [first.format(ISO)]
  const step = mode === 'weekly' ? 7 : 1
  const out = []
  const cur = first.clone()
  const cap = first.clone().add(maxDays, 'day')
  while (!cur.isAfter(last) && !cur.isAfter(cap)) {
    out.push(cur.format(ISO))
    cur.add(step, 'day')
  }
  return out
}

/**
 * Weekly template -> list of { date, start, end, ... } blocks.
 * template: { 1: [{ start, end, typeId, posId, perSlotTime, perSlotAmount }], ... }
 * keyed by ISO weekday (1 = Monday .. 7 = Sunday).
 * Past dates and dates that already have an overlapping block are skipped.
 */
export function expandTemplate (template, from, to, existing = {}, today = clinicNow().format(ISO)) {
  const out = []
  const skipped = []
  const start = moment(from, ISO, true)
  const end = moment(to, ISO, true)
  if (!start.isValid() || !end.isValid()) return { blocks: out, skipped }
  for (const cur = start.clone(); !cur.isAfter(end); cur.add(1, 'day')) {
    const date = cur.format(ISO)
    if (date < today) continue
    const ranges = (template && template[cur.isoWeekday()]) || []
    ranges.forEach((r) => {
      if (!r || toMin(r.end) <= toMin(r.start)) return
      const clash = ((existing[date] && existing[date].blocks) || []).some((b) => overlaps(b.start, b.end, r.start, r.end))
      if (clash) skipped.push({ date, ...r })
      else out.push({ date, ...r })
    })
  }
  return { blocks: out, skipped }
}

export function overlaps (aStart, aEnd, bStart, bEnd) {
  return toMin(aStart) < toMin(bEnd) && toMin(aEnd) > toMin(bStart)
}

// Copy one weekday's ranges to other weekdays (template editor)
export function copyDay (template, fromDay, toDays) {
  const next = { ...template }
  const src = (template[fromDay] || []).map((r) => ({ ...r }))
  ;(toDays || []).forEach((d) => {
    if (Number(d) !== Number(fromDay)) next[d] = src.map((r) => ({ ...r }))
  })
  return next
}

// Payload for POST schedule. TB-123 (W35): wall values as 'YYYY-MM-DD' + 'HH:mm'.
export function createPayload ({ date, start, end, typeId, posId, perSlotTime, perSlotAmount, providerId }) {
  const body = {
    consultationTypeId: typeId,
    replacementProvider: null,
    ...toWallPayload({ date, start, end }),
    posId,
    perSlotTime: parseInt(perSlotTime, 10),
    perSlotAmount: Number(perSlotAmount),
    repeat: 0
  }
  if (providerId) body.providerId = providerId
  return body
}

// Runs async task factories with at most `limit` in flight; never rejects.
export async function runLimited (tasks, limit = 4, onProgress) {
  const results = new Array(tasks.length)
  let next = 0
  let done = 0
  const worker = async () => {
    while (next < tasks.length) {
      const i = next++
      try {
        results[i] = { ok: true, value: await tasks[i]() }
      } catch (error) {
        results[i] = { ok: false, error }
      }
      done++
      if (onProgress) onProgress(done, tasks.length)
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, tasks.length) }, worker))
  return results
}

// TB-159: a video visit is joinable from CALL_WINDOW_MINUTES before its start
// until CALL_WINDOW_MINUTES after its scheduled end (clinic wall time). The
// server is the authority; this only decides which button to show.
export const CALL_WINDOW_MINUTES = 10

/**
 * Join window of a video visit. appt: { date, start, end }, now: clinic wall
 * clock moment. state: 'early' | 'open' | 'late' | 'unknown'; opensAt and
 * closesAt are "HH:mm"; message is the disabled-button text.
 */
export function callWindow (appt, now = clinicNow()) {
  const startAt = appt ? moment.utc(`${appt.date} ${appt.start}`, 'YYYY-MM-DD HH:mm') : null
  if (!startAt || !startAt.isValid()) return { state: 'unknown', opensAt: '', closesAt: '', message: '' }
  const opens = startAt.clone().subtract(CALL_WINDOW_MINUTES, 'minutes')
  let endAt = moment.utc(`${appt.date} ${appt.end}`, 'YYYY-MM-DD HH:mm')
  if (endAt.isValid() && !endAt.isAfter(startAt)) endAt = endAt.add(1, 'day')
  const closes = endAt.isValid() ? endAt.clone().add(CALL_WINDOW_MINUTES, 'minutes') : null
  const opensAt = opens.format('HH:mm')
  const closesAt = closes ? closes.format('HH:mm') : ''
  if (now.isBefore(opens)) return { state: 'early', opensAt, closesAt, message: `Opens at ${formatClock(opensAt)}` }
  if (closes && now.isAfter(closes)) return { state: 'late', opensAt, closesAt, message: `Closed at ${formatClock(closesAt)}` }
  return { state: 'open', opensAt, closesAt, message: '' }
}

/** callWindow of a raw appointment ({ startTime, endTime } wall ISO). */
export function callWindowOf (a, now = clinicNow()) {
  if (!a || !a.startTime) return callWindow(null, now)
  return callWindow({ date: apptDate(a.startTime), start: apptTime(a.startTime), end: apptTime(a.endTime || a.startTime) }, now)
}

/**
 * Actions the side panel offers for an appointment right now.
 * appt: { status, confirmed, type, date, start }.
 */
export function actionsFor (appt, now = clinicNow()) {
  if (!appt) return []
  const out = []
  const active = isActive(appt.status)
  const startAt = moment.utc(`${appt.date} ${appt.start}`, 'YYYY-MM-DD HH:mm')
  const started = startAt.isValid() && !startAt.isAfter(now)
  // TB-159: a paid, booked video visit is joinable only inside its call window.
  if (active && appt.type === 'video' && callWindow(appt, now).state === 'open') out.push('video')
  if (active && started) out.push('complete')
  if (active) out.push('reschedule', 'cancel')
  if (appt.status === 'completed' || appt.status === 'awaiting-invoice') out.push('invoice')
  return out
}

export function isEnded (status) {
  return ENDED.includes(status)
}
